import { useEffect, useState, Fragment } from "react";
import { toast } from "sonner";
import { ShieldCheck, FloppyDisk, ArrowCounterClockwise } from "@phosphor-icons/react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { MENU_REGISTRY, CONFIGURABLE_ROLES, defaultAccessForRole } from "../lib/menuRegistry";
import { roleLabel } from "../lib/rbac";

// Rows to render: each group + its selectable "items" (link groups get a single synthetic row).
const ROWS = MENU_REGISTRY.map((g) => ({
  code: g.code, label: g.label, type: g.type,
  items: g.type === "link" ? [{ to: g.to, label: `${g.label} (home)` }] : g.items,
}));
const ITEM_CODE = {}; // path -> group code
ROWS.forEach((g) => g.items.forEach((it) => { ITEM_CODE[it.to] = g.code; }));

export default function AccessControl() {
  const [sel, setSel] = useState(null);   // { role: Set(paths) }
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/menu-access");
        const configs = r.data?.configs || {};
        const next = {};
        for (const role of CONFIGURABLE_ROLES) {
          const cfg = configs[role];
          const items = cfg ? (cfg.items || []) : defaultAccessForRole(role).items;
          next[role] = new Set(items);
        }
        setSel(next);
      } catch (e) { toast.error(e.response?.data?.detail || "Failed to load access config"); }
    })();
  }, []);

  const setRoleItems = (role, updater) => setSel((prev) => {
    const s = new Set(prev[role]); updater(s); return { ...prev, [role]: s };
  });
  const toggleItem = (role, to) => setRoleItems(role, (s) => { s.has(to) ? s.delete(to) : s.add(to); });
  const toggleGroup = (role, g) => setRoleItems(role, (s) => {
    const allOn = g.items.every((it) => s.has(it.to));
    g.items.forEach((it) => (allOn ? s.delete(it.to) : s.add(it.to)));
  });
  const resetRole = (role) => setSel((prev) => ({ ...prev, [role]: new Set(defaultAccessForRole(role).items) }));

  const save = async () => {
    setSaving(true);
    try {
      for (const role of CONFIGURABLE_ROLES) {
        const items = [...sel[role]];
        const groups = [...new Set(items.map((p) => ITEM_CODE[p]).filter(Boolean))];
        await api.put("/menu-access", { role, groups, items });
      }
      toast.success("Access saved · users see changes on next page load");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const th = { padding: "10px 12px", fontSize: 12, fontWeight: 800, color: "#fff", textAlign: "center", whiteSpace: "nowrap" };
  const td = { padding: "7px 12px", fontSize: 12.5, borderBottom: "1px solid #F1F5F9", textAlign: "center" };

  return (
    <Layout>
      <div data-testid="access-page" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, display: "flex", alignItems: "center", gap: 8 }}><ShieldCheck size={22} weight="bold" /> Access Control (RBAC)</h1>
            <p style={{ fontSize: 13, color: "#64748B", margin: "4px 0 0", maxWidth: 820 }}>
              Show or hide every page per role. Tick a menu to grant the whole group, or expand and pick individual pages. <strong>Super Admin</strong> always has full access and can never be locked out or downgraded here (not a configurable role). <strong>Admins</strong> can view and edit this matrix; <strong>Admin</strong>, <strong>Finance</strong> and every other role are fully configurable. Granting the Super Admin role stays Super-Admin-only. Users see changes on their next page load. Backend API role-guards remain a security backstop.
            </p>
          </div>
          <button data-testid="access-save" onClick={save} disabled={saving || !sel} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 18px", border: "none", borderRadius: 8, background: "#0EA5E9", color: "#fff", fontWeight: 800, fontSize: 13.5, cursor: "pointer" }}><FloppyDisk size={16} weight="bold" /> {saving ? "Saving…" : "Save changes"}</button>
        </div>

        <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table data-testid="access-table" style={{ borderCollapse: "collapse", width: "100%", minWidth: 720 }}>
              <thead>
                <tr style={{ background: "#0F172A" }}>
                  <th style={{ ...th, textAlign: "left", position: "sticky", left: 0, background: "#0F172A", minWidth: 260 }}>Menu / Sub‑menu</th>
                  {CONFIGURABLE_ROLES.map((role) => (
                    <th key={role} style={th}>{roleLabel(role)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {!sel && <tr><td colSpan={CONFIGURABLE_ROLES.length + 1} style={{ ...td, padding: 40, color: "#94A3B8" }}>Loading…</td></tr>}
                {sel && ROWS.map((g) => (
                  <Fragment key={g.code}>
                    <tr style={{ background: "#F1F5F9" }}>
                      <td style={{ ...td, textAlign: "left", fontWeight: 800, color: "#0F172A", position: "sticky", left: 0, background: "#F1F5F9" }} data-testid={`access-group-${g.code}`}>{g.label}</td>
                      {CONFIGURABLE_ROLES.map((role) => {
                        const allOn = g.items.every((it) => sel[role].has(it.to));
                        const someOn = g.items.some((it) => sel[role].has(it.to));
                        return (
                          <td key={role} style={{ ...td, background: "#F1F5F9" }}>
                            <input type="checkbox" data-testid={`access-grp-${g.code}-${role}`} checked={allOn} ref={(el) => { if (el) el.indeterminate = !allOn && someOn; }} onChange={() => toggleGroup(role, g)} style={{ cursor: "pointer", width: 16, height: 16 }} />
                          </td>
                        );
                      })}
                    </tr>
                    {g.type !== "link" && g.items.map((it) => (
                      <tr key={it.to}>
                        <td style={{ ...td, textAlign: "left", color: "#334155", paddingLeft: 30, position: "sticky", left: 0, background: "#fff" }}>{it.label}</td>
                        {CONFIGURABLE_ROLES.map((role) => (
                          <td key={role} style={td}>
                            <input type="checkbox" data-testid={`access-item-${it.to}-${role}`} checked={sel[role].has(it.to)} onChange={() => toggleItem(role, it.to)} style={{ cursor: "pointer", width: 15, height: 15 }} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
              {sel && (
                <tfoot>
                  <tr>
                    <td style={{ ...td, textAlign: "left", fontWeight: 700, color: "#64748B", position: "sticky", left: 0, background: "#fff" }}>Reset a role to defaults</td>
                    {CONFIGURABLE_ROLES.map((role) => (
                      <td key={role} style={td}>
                        <button data-testid={`access-reset-${role}`} onClick={() => resetRole(role)} title="Reset to default" style={{ border: "1px solid #CBD5E1", background: "#fff", borderRadius: 6, padding: "3px 6px", cursor: "pointer", color: "#475569" }}><ArrowCounterClockwise size={13} /></button>
                      </td>
                    ))}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
